import { useQuery } from "@tanstack/react-query";
import { DxCard, KpiTile } from "../../components/dx";
import { AlertTriangle } from "../../components/icons";
import { api, authHeaders, describeError } from "../../lib/api";
import { fetchDocTypes, placeKey } from "../../lib/dashboard-data";
import { num } from "../../lib/format";
import { rangeKey } from "../../lib/stats-range";
import type { DashCtx } from "./ctx";

async function fetchRegistryStatus() {
  const { data, error, response } = await api.GET("/v1/registry/status", { headers: authHeaders() });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

/** ชนิดเอกสารที่เป็นการแจ้งพักเซา/เข้า-ออก/ย้าย (ADR-0027 Phase 5) — นับจากคำร้องในระบบนี้จริง */
/** ข้อความสั้นใต้การ์ด — ข้อความเต็มจาก server อยู่ในแถบบนแล้ว */
const TILE_NA = "ຕ້ອງເຊື່ອມຕໍ່ທະບຽນລາດສະດອນກ່ອນ";

const NOTICES = [
  { code: "TEMP_STAY", label: "ແຈ້ງພັກເຊົາຊົ່ວຄາວ", tone: "navy" as const },
  { code: "PERSON_INOUT", label: "ແຈ້ງເຂົ້າ-ອອກ", tone: "teal" as const },
  { code: "A14", label: "ຍົກຍ້າຍ ເຂົ້າ-ອອກ", tone: "gold" as const },
];

/**
 * ແຖບ ປະຊາກອນ — ตัวเลขประชากร/ผู้พักเซาจริงต้องมาจากทะเบียนราษฎร์ของกระทรวงภายใน ซึ่งยังไม่ได้เชื่อม
 * (`port.CivilRegistry` → NotConnected) จึงแสดงช่องตาม mockup แต่บอกว่ายังไม่มีข้อมูล — ห้ามใส่ตัวเลขตัวอย่าง
 * ส่วนที่มีจริงคือจำนวนคำร้องแจ้งพักเซา/เข้า-ออก/ย้าย ที่ยื่นผ่านระบบนี้
 */
export function PopulationTab({ ctx }: { ctx: DashCtx }) {
  const { range, place, here } = ctx;
  const registry = useQuery({ queryKey: ["registry", "status"], queryFn: fetchRegistryStatus, staleTime: 5 * 60_000 });
  const types = useQuery({
    queryKey: ["stats", "document-types", `${rangeKey(range)}|${placeKey(place)}`, 100],
    queryFn: () => fetchDocTypes(range, place, 100),
  });
  const connected = registry.data?.connected === true;
  const na = registry.data?.message_lo ?? "ຍັງບໍ່ໄດ້ເຊື່ອມຕໍ່ທະບຽນລາດສະດອນ";

  return (
    <>
      {!connected ? (
        <p className="dash-alert dash-alert--info">
          <AlertTriangle size={16} />
          <span>
            {registry.isPending ? "ກຳລັງກວດສະຖານະທະບຽນລາດສະດອນ…" : na} — ຈຳນວນປະຊາກອນ ແລະ ຜູ້ພັກເຊົາ ຈະສະແດງເມື່ອເຊື່ອມຕໍ່ແລ້ວ
          </span>
        </p>
      ) : null}

      <div className="dx-kpis dx-kpis--4">
        <KpiTile label="ປະຊາກອນທັງໝົດ" value={undefined} tone="navy" na={TILE_NA} />
        <KpiTile label="ຜູ້ພັກເຊົາຊົ່ວຄາວ (ຕອນນີ້)" value={undefined} tone="teal" na={TILE_NA} />
        <KpiTile label="ຄົນຕ່າງດ້າວ" value={undefined} tone="gold" na={TILE_NA} />
        <KpiTile label="ຄົວເຮືອນ" value={undefined} tone="green" na={TILE_NA} />
      </div>

      <div className="dx-gap">
        <DxCard
          title="ຄຳຮ້ອງແຈ້ງພັກເຊົາ ແລະ ການເຄື່ອນຍ້າຍ"
          sub={`${here.name} · ${ctx.period} · ນັບຈາກຄຳຮ້ອງທີ່ຍື່ນຜ່ານລະບົບນີ້ເທົ່ານັ້ນ`}
        >
          {types.error ? <p className="field-error">ໂຫຼດບໍ່ສຳເລັດ: {types.error.message}</p> : null}
          <div className="dx-kpis dx-kpis--3">
            {NOTICES.map((n) => {
              const row = types.data?.find((d) => d.code === n.code);
              return (
                <KpiTile
                  key={n.code}
                  label={n.label}
                  value={types.isPending ? undefined : num(row?.requests ?? 0)}
                  unit="ຄຳຮ້ອງ"
                  tone={n.tone}
                  hint={row ? `ອອກແລ້ວ ${num(row.issued)}` : "ບໍ່ມີຄຳຮ້ອງໃນຊ່ວງນີ້"}
                />
              );
            })}
          </div>
          <p className="dx-note">ບໍ່ແມ່ນຈຳນວນຄົນ — ຄຳຮ້ອງໜຶ່ງອາດມີຫຼາຍຄົນ ຫຼື ຄົນດຽວແຈ້ງຫຼາຍຄັ້ງ</p>
        </DxCard>
      </div>
    </>
  );
}
